import React, { useMemo } from 'react';
import {
    DmsTextField,
    DmsSwitch,
    DmsSelect,
    useGet,
    intl,
    fetchData,
    HTTP_STATUS
} from 'dmsbasereact';
import { SettingsViewEdit } from '../../components/SettingsLayouts';
import { Grid, Typography, Box, Divider } from '@mui/material';

/**
 * SecuritySettings - Rate Limiter and MFA Protection configuration.
 * Uses view/edit mode pattern for consistent UX.
 */

/**
 * Get fail mode options for select component
 * @returns {Array} Array of {value, label} objects
 */
const getFailModeOptions = () => [
    { value: 'open', label: intl.get('FailModeOpen').d('Open - Allow requests when Redis unavailable') },
    { value: 'closed', label: intl.get('FailModeClosed').d('Closed - Block requests when Redis unavailable') }
];

/**
 * ViewContent - Display security settings in read-only mode
 */
const ViewContent = ({ data }) => {
    return (
        <Box>
            {/* Rate Limiter Section */}
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {intl.get('RateLimiter').d('Rate Limiter')}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                {intl.get('RateLimiterDescription').d(
                    'Controls how many requests can be made to the API within a time window. Protects against brute force and DDoS attacks.'
                )}
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                        {intl.get('Enabled').d('Enabled')}
                    </Typography>
                    <Typography variant="body1">
                        {data.rate_limiter_enabled ? intl.get('Enabled').d('Enabled') : intl.get('Disabled').d('Disabled')}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                        {intl.get('DefaultLimit').d('Request Limit')}
                    </Typography>
                    <Typography variant="body1">
                        {data.rate_limiter_default_limit ?? 100}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                        {intl.get('TimeWindow').d('Time Window (seconds)')}
                    </Typography>
                    <Typography variant="body1">
                        {data.rate_limiter_default_window ?? 60}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                        {intl.get('FailMode').d('Fail Mode')}
                    </Typography>
                    <Typography variant="body1">
                        {data.rate_limiter_fail_mode === 'open'
                            ? intl.get('Open').d('Open')
                            : intl.get('Closed').d('Closed')}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                        {intl.get('LogViolations').d('Log rate limit violations')}
                    </Typography>
                    <Typography variant="body1">
                        {data.rate_limiter_log_violations ? intl.get('Enabled').d('Enabled') : intl.get('Disabled').d('Disabled')}
                    </Typography>
                </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* MFA Protection Section */}
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {intl.get('MFAProtection').d('MFA Brute Force Protection')}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                {intl.get('MFAProtectionDescription').d(
                    'Protects against brute force attacks on MFA verification. Locks accounts temporarily after multiple failed attempts.'
                )}
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                        {intl.get('Enabled').d('Enabled')}
                    </Typography>
                    <Typography variant="body1">
                        {data.mfa_protection_enabled ? intl.get('Enabled').d('Enabled') : intl.get('Disabled').d('Disabled')}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                        {intl.get('MaxAttempts').d('Max Failed Attempts')}
                    </Typography>
                    <Typography variant="body1">
                        {data.mfa_max_attempts ?? 5}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                        {intl.get('LockoutDuration').d('Lockout Duration (seconds)')}
                    </Typography>
                    <Typography variant="body1">
                        {data.mfa_lockout_duration ?? 900}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                        {intl.get('AttemptWindow').d('Attempt Window (seconds)')}
                    </Typography>
                    <Typography variant="body1">
                        {data.mfa_attempt_window ?? 300}
                    </Typography>
                </Grid>
            </Grid>
        </Box>
    );
};

/**
 * EditContent - Display security settings as editable form fields
 */
const EditContent = ({ formData, onFieldChange }) => {
    // Handle DmsSwitch which passes (name, stringValue)
    const handleSwitchChange = (name, value) => {
        const boolValue = value === 'true' || value === true;
        onFieldChange(name, boolValue);
    };

    const failModeOptions = useMemo(() => getFailModeOptions(), []);

    return (
        <Box>
            {/* Rate Limiter Section */}
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {intl.get('RateLimiter').d('Rate Limiter')}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                {intl.get('RateLimiterDescription').d(
                    'Controls how many requests can be made to the API within a time window. Protects against brute force and DDoS attacks.'
                )}
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                    <DmsSwitch
                        name="rate_limiter_enabled"
                        label={intl.get('Enabled').d('Enabled')}
                        checked={formData.rate_limiter_enabled ?? true}
                        onChange={handleSwitchChange}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsTextField
                        name="rate_limiter_default_limit"
                        label={intl.get('DefaultLimit').d('Request Limit')}
                        type="number"
                        fullWidth
                        value={formData.rate_limiter_default_limit ?? 100}
                        onChange={(e) => onFieldChange('rate_limiter_default_limit', e.target.value)}
                        helperText={intl.get('RequestsPerWindowHelp').d('Max requests allowed per time window')}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsTextField
                        name="rate_limiter_default_window"
                        label={intl.get('TimeWindow').d('Time Window (seconds)')}
                        type="number"
                        fullWidth
                        value={formData.rate_limiter_default_window ?? 60}
                        onChange={(e) => onFieldChange('rate_limiter_default_window', e.target.value)}
                        helperText={intl.get('TimeWindowHelp').d('60 = 1 minute, 300 = 5 minutes')}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsSelect
                        name="rate_limiter_fail_mode"
                        label={intl.get('FailMode').d('Fail Mode')}
                        fullWidth
                        value={formData.rate_limiter_fail_mode ?? 'open'}
                        onChange={(value) => onFieldChange('rate_limiter_fail_mode', value)}
                        options={failModeOptions}
                        helperText={intl.get('FailModeHelp').d('Behavior when Redis is unavailable')}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsSwitch
                        name="rate_limiter_log_violations"
                        label={intl.get('LogViolations').d('Log rate limit violations')}
                        checked={formData.rate_limiter_log_violations ?? true}
                        onChange={handleSwitchChange}
                    />
                </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* MFA Protection Section */}
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {intl.get('MFAProtection').d('MFA Brute Force Protection')}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                {intl.get('MFAProtectionDescription').d(
                    'Protects against brute force attacks on MFA verification. Locks accounts temporarily after multiple failed attempts.'
                )}
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <DmsSwitch
                        name="mfa_protection_enabled"
                        label={intl.get('Enabled').d('Enabled')}
                        checked={formData.mfa_protection_enabled ?? true}
                        onChange={handleSwitchChange}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsTextField
                        name="mfa_max_attempts"
                        label={intl.get('MaxAttempts').d('Max Failed Attempts')}
                        type="number"
                        fullWidth
                        value={formData.mfa_max_attempts ?? 5}
                        onChange={(e) => onFieldChange('mfa_max_attempts', e.target.value)}
                        helperText={intl.get('MaxAttemptsHelp').d('Failed attempts before lockout')}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsTextField
                        name="mfa_lockout_duration"
                        label={intl.get('LockoutDuration').d('Lockout Duration (seconds)')}
                        type="number"
                        fullWidth
                        value={formData.mfa_lockout_duration ?? 900}
                        onChange={(e) => onFieldChange('mfa_lockout_duration', e.target.value)}
                        helperText={intl.get('LockoutDurationHelp').d('900 = 15 min, 1800 = 30 min')}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsTextField
                        name="mfa_attempt_window"
                        label={intl.get('AttemptWindow').d('Attempt Window (seconds)')}
                        type="number"
                        fullWidth
                        value={formData.mfa_attempt_window ?? 300}
                        onChange={(e) => onFieldChange('mfa_attempt_window', e.target.value)}
                        helperText={intl.get('AttemptWindowHelp').d('Time window for counting failures')}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export const SecuritySettings = () => {
    const { data, isLoading, refetch } = useGet('/security-settings', null, true);

    const handleSave = async (payload) => {
        try {
            const response = await fetchData('/security-settings', 'PUT', true, payload, false, true);
            if (response.status === HTTP_STATUS.OK_200) {
                return response.data.data;
            }
            return null;
        } catch (error) {
            console.error('Error saving security settings:', error);
            return null;
        }
    };

    return (
        <SettingsViewEdit
            data={data?.data}
            loading={isLoading}
            title={intl.get('SecuritySettings').d('Security Settings')}
            description={intl.get('SecuritySettingsDescription').d(
                'Configure security features including rate limiting and MFA protection.'
            )}
            viewContent={ViewContent}
            editContent={EditContent}
            prepareFormData={(data) => ({
                rate_limiter_enabled: data.rate_limiter_enabled ?? true,
                rate_limiter_default_limit: data.rate_limiter_default_limit ?? 100,
                rate_limiter_default_window: data.rate_limiter_default_window ?? 60,
                rate_limiter_fail_mode: data.rate_limiter_fail_mode ?? 'open',
                rate_limiter_log_violations: data.rate_limiter_log_violations ?? true,
                mfa_protection_enabled: data.mfa_protection_enabled ?? true,
                mfa_max_attempts: data.mfa_max_attempts ?? 5,
                mfa_lockout_duration: data.mfa_lockout_duration ?? 900,
                mfa_attempt_window: data.mfa_attempt_window ?? 300
            })}
            buildPayload={(formData) => ({
                rate_limiter_enabled: formData.rate_limiter_enabled,
                rate_limiter_default_limit: parseInt(formData.rate_limiter_default_limit, 10),
                rate_limiter_default_window: parseInt(formData.rate_limiter_default_window, 10),
                rate_limiter_fail_mode: formData.rate_limiter_fail_mode,
                rate_limiter_log_violations: formData.rate_limiter_log_violations,
                mfa_protection_enabled: formData.mfa_protection_enabled,
                mfa_max_attempts: parseInt(formData.mfa_max_attempts, 10),
                mfa_lockout_duration: parseInt(formData.mfa_lockout_duration, 10),
                mfa_attempt_window: parseInt(formData.mfa_attempt_window, 10)
            })}
            onSave={handleSave}
            onDataUpdated={refetch}
            confirmMessage={intl.get('ConfirmSaveSecurityMessage').d(
                'Are you sure you want to save the security settings changes?'
            )}
        />
    );
};
